/**
 * Filtros salvos do Dashboard (por aba/seção), client-only, escopados ao workspace.
 * Separado de `savedFilters.js` (chat/busca).
 */

import {
  readWorkspaceLocalJson,
  writeWorkspaceLocalJson,
  WS_KEY,
} from "./workspaceStorage";
import { normalizeSearchFilterState } from "./searchFilterState";
import { normalizeCampaignFilterState } from "./dashboardCampaignFilterState";
import { normalizeSalesFilterState } from "./dashboardSalesFilterState";
import { normalizeClientsFilterState } from "./dashboardClientsFilterState";
import { normalizeProdutosFilterState } from "./dashboardProdutosFilterState";
import { normalizePassagensFilterState } from "./dashboardPassagensFilterState";
import { normalizeSurveysFilterState } from "./dashboardSurveysFilterState";

export const DASHBOARD_SAVED_FILTER_SECTIONS = [
  "atendimento",
  "live",
  "origem",
  "vendas",
  "clientes",
  "produtos",
  "venda-passagens",
  "surveys",
];

/** live e atendimento compartilham o mesmo estado avançado. */
export function resolveDashboardSavedSection(section) {
  const s = String(section || "").trim();
  if (s === "live") return "atendimento";
  return s;
}

function normalizeStateForSection(section, raw) {
  const key = resolveDashboardSavedSection(section);
  switch (key) {
    case "atendimento":
      return normalizeSearchFilterState({ ...(raw || {}), q: "" });
    case "origem":
      return normalizeCampaignFilterState(raw);
    case "vendas":
      return normalizeSalesFilterState(raw);
    case "clientes":
      return normalizeClientsFilterState(raw);
    case "produtos":
      return normalizeProdutosFilterState(raw);
    case "venda-passagens":
      return normalizePassagensFilterState(raw);
    case "surveys":
      return normalizeSurveysFilterState(raw);
    default:
      return raw && typeof raw === "object" ? { ...raw } : {};
  }
}

/** Estado comparável (ignora labels / metadados de UI). */
export function comparableDashboardFilterState(section, raw) {
  const normalized = normalizeStateForSection(section, raw);
  if (!normalized || typeof normalized !== "object") return {};
  const { labels, ...rest } = normalized;
  return rest;
}

function stableStringify(value) {
  if (value == null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map((v) => stableStringify(v)).join(",")}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(",")}}`;
}

export function isSameDashboardFilterState(section, a, b) {
  return (
    stableStringify(comparableDashboardFilterState(section, a)) ===
    stableStringify(comparableDashboardFilterState(section, b))
  );
}

/** true se a combinação atual já existe entre os salvos da seção. */
export function isCurrentDashboardFilterSaved(section, currentState) {
  const entries = getDashboardSavedFilters(section);
  return entries.some((entry) => isSameDashboardFilterState(section, currentState, entry.state));
}

function loadAll() {
  try {
    const parsed = readWorkspaceLocalJson(WS_KEY.DASHBOARD_SAVED_FILTERS, []);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persist(list) {
  writeWorkspaceLocalJson(WS_KEY.DASHBOARD_SAVED_FILTERS, list);
}

function newId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `dsf-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function sortEntries(list) {
  return [...list].sort((a, b) => {
    const fa = a.favorito ? 1 : 0;
    const fb = b.favorito ? 1 : 0;
    if (fb !== fa) return fb - fa;
    return String(b.createdAt || "").localeCompare(String(a.createdAt || ""));
  });
}

/**
 * @param {string} section
 * @returns {Array<{ id, nome, favorito, createdAt, section, state }>}
 */
export function getDashboardSavedFilters(section) {
  const key = resolveDashboardSavedSection(section);
  if (!key) return [];
  return sortEntries(
    loadAll()
      .filter((f) => f && typeof f === "object" && f.id && f.nome)
      .filter((f) => resolveDashboardSavedSection(f.section) === key)
      .map((f) => ({
        ...f,
        section: key,
        state: normalizeStateForSection(key, f.state || {}),
      }))
  );
}

export function saveDashboardFilter({ section, nome, state, favorito = false }) {
  const key = resolveDashboardSavedSection(section);
  const trimmed = String(nome || "").trim();
  if (!key) throw new Error("Seção de filtro inválida");
  if (!trimmed) throw new Error("Informe um nome para o filtro");

  const entry = {
    id: newId(),
    nome: trimmed,
    favorito: Boolean(favorito),
    createdAt: new Date().toISOString(),
    section: key,
    state: normalizeStateForSection(key, state),
  };

  persist([...loadAll(), entry]);
  return entry;
}

export function deleteDashboardSavedFilter(id) {
  persist(loadAll().filter((f) => String(f.id) !== String(id)));
}

export function toggleDashboardSavedFilterFavorite(id) {
  persist(
    loadAll().map((f) =>
      String(f.id) === String(id) ? { ...f, favorito: !f.favorito } : f
    )
  );
}

export function renameDashboardSavedFilter(id, nome) {
  const trimmed = String(nome || "").trim();
  if (!trimmed) return;
  persist(
    loadAll().map((f) => (String(f.id) === String(id) ? { ...f, nome: trimmed } : f))
  );
}
